import { Mail, Phone } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type ProjectTeamMember,
  useProjectTeam,
  useUpdateProjectTeamMember,
} from "@/hooks/use-project-team";
import { cn } from "@/lib/utils";

const PRIMARY_KEYS = new Set(["project_manager", "superintendent"]);

const COLORS = [
  "#475569",
  "#7c3aed",
  "#0f766e",
  "#b45309",
  "#1d4ed8",
  "#9333ea",
  "#0891b2",
  "#be185d",
];

function colorFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  const idx = Math.abs(hash) % COLORS.length;
  return COLORS[idx] ?? "#475569";
}

function initials(m: ProjectTeamMember): string {
  const f = (m.contact.firstName ?? "").trim();
  const l = (m.contact.lastName ?? "").trim();
  return `${f[0] ?? ""}${l[0] ?? ""}`.toUpperCase() || "?";
}

function mailtoHref(email: string | null | undefined): string | null {
  const v = (email ?? "").trim();
  if (!v) return null;
  return `mailto:${encodeURIComponent(v)}`;
}

function telHref(phone: string | null | undefined): string | null {
  const v = (phone ?? "").trim();
  if (!v) return null;
  // Preserve a leading "+", strip everything else that isn't a digit.
  const sanitized = (v.startsWith("+") ? "+" : "") + v.replace(/[^\d]/g, "");
  if (sanitized === "" || sanitized === "+") return null;
  return `tel:${sanitized}`;
}

interface TeamCardProps {
  projectId: string;
}

export function TeamCard({ projectId }: TeamCardProps) {
  const { data: team, isLoading } = useProjectTeam(projectId);
  const updateMember = useUpdateProjectTeamMember(projectId);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-5 space-y-3">
        <Skeleton className="h-4 w-32" />
        {["row-a", "row-b"].map((k) => (
          <Skeleton key={k} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  const members = team ?? [];
  if (members.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-5">
        <h3 className="text-[15px] font-semibold mb-1">Project team</h3>
        <p className="text-[12px] text-muted-foreground">No team members assigned yet.</p>
      </div>
    );
  }

  const primaries = members.filter((m) => PRIMARY_KEYS.has(m.role.key));
  const others = members.filter((m) => !PRIMARY_KEYS.has(m.role.key));

  const toggleOnSite = (member: ProjectTeamMember) => {
    updateMember.mutate({ memberId: member.id, patch: { onSite: !member.onSite } });
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[15px] font-semibold">Project team</h3>
      </div>
      <p className="text-[12px] text-muted-foreground">Prominent Builders · GC team</p>

      {primaries.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {primaries.map((m) => (
            <li key={m.id} className="flex items-center gap-3">
              <span
                className="h-9 w-9 rounded-full text-white text-[12px] font-semibold inline-flex items-center justify-center shrink-0"
                style={{ background: colorFor(m.id) }}
              >
                {initials(m)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="text-[13.5px] font-medium truncate">
                    {m.contact.firstName} {m.contact.lastName}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleOnSite(m)}
                    disabled={updateMember.isPending}
                    className={cn(
                      "text-[10px] font-semibold border rounded px-1 py-px transition-colors",
                      m.onSite
                        ? "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                        : "text-muted-foreground bg-transparent border-border hover:bg-secondary",
                    )}
                  >
                    {m.onSite ? "On site" : "Mark on site"}
                  </button>
                </div>
                <div className="text-[11.5px] text-muted-foreground truncate">{m.role.label}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {(() => {
                  const mail = mailtoHref(m.contact.email);
                  return mail ? (
                    <a
                      href={mail}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"
                      aria-label={`Email ${m.contact.firstName}`}
                    >
                      <Mail className="h-3.5 w-3.5" />
                    </a>
                  ) : null;
                })()}
                {(() => {
                  const tel = telHref(m.contact.phone);
                  return tel ? (
                    <a
                      href={tel}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"
                      aria-label={`Call ${m.contact.firstName}`}
                    >
                      <Phone className="h-3.5 w-3.5" />
                    </a>
                  ) : null;
                })()}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {others.length > 0 ? (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground font-semibold mb-2">
            Also on this project
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {others.map((m) => (
              <li
                key={m.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/40 pl-0.5 pr-2 py-0.5"
              >
                <span
                  className="h-5 w-5 rounded-full text-white text-[9px] font-semibold inline-flex items-center justify-center"
                  style={{ background: colorFor(m.id) }}
                >
                  {initials(m)}
                </span>
                <span className="text-[11.5px]">
                  {m.contact.firstName} {m.contact.lastName}
                </span>
                <span className="text-[10.5px] text-muted-foreground">· {m.role.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
